import { useEffect, useRef } from 'react'

import { useAuthStore } from '@/lib/auth/store'

/**
 * Idle-session timeout. After IDLE_TIMEOUT_MS with no user interaction in
 * any tab we clear the session and hard-redirect to /login with reason
 * 'inactivity', so LoginPage can explain why they were signed out.
 *
 * Tabs share one session (localStorage), so they share one idle clock too:
 * each tab writes its latest activity to LAST_ACTIVITY_KEY, and a tab whose
 * own timer runs out checks that first. An idle background tab therefore
 * never signs out a user who is working in another one.
 *
 * Mount this once inside the authed layout (AppLayout) so it only runs while
 * a session exists — it must not arm a logout timer on the login screen.
 *
 * Why a hard redirect (window.location) rather than navigate(): same reason
 * as the 401 handler in lib/api/client.ts — TanStack Router's auth guard only
 * re-runs on navigation, and a full load tears the authed tree down cleanly
 * and drops the previous user's React Query cache.
 */
export const IDLE_TIMEOUT_MS =
  Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 30 * 60 * 1000

/** The latest activity in any tab, as epoch millis. */
export const LAST_ACTIVITY_KEY = 'grav-last-activity'

function readSharedActivity(): number {
  try {
    return Number(window.localStorage.getItem(LAST_ACTIVITY_KEY)) || 0
  } catch {
    return 0
  }
}

function writeSharedActivity(at: number) {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at))
  } catch {
    // Storage blocked: this tab keeps its own clock.
  }
}

// High-frequency events (mousemove, scroll) are throttled below so we don't
// reset the timer on every pixel of movement.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const

const THROTTLE_MS = 1_000

export function useIdleLogout() {
  const logout = useAuthStore((s) => s.logout)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastResetRef = useRef(0)

  useEffect(() => {
    function arm(delay: number) {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(expire, delay)
    }

    function expire() {
      // Another tab may have been in use since this one's last event.
      const idleFor = Date.now() - Math.max(lastResetRef.current, readSharedActivity())
      if (idleFor < IDLE_TIMEOUT_MS) {
        arm(IDLE_TIMEOUT_MS - idleFor)
        return
      }
      logout('inactivity')
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login')
      }
    }

    function reset() {
      const now = Date.now()
      if (now - lastResetRef.current < THROTTLE_MS) return
      lastResetRef.current = now
      writeSharedActivity(now)
      arm(IDLE_TIMEOUT_MS)
    }

    reset() // arm the timer on mount
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, reset, { passive: true }),
    )
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [logout])
}
